export interface SeriesDef {
  label: string;
  color: string;
}

export interface GraphRenderer {
  push(values: number[], time: number): void;
  reset(): void;
  draw(): void;
  resize(w: number, h: number): void;
  setViewport(tStart: number | null, tEnd: number | null): void;
  setMarkers(markers: { time: number; type: 'full' | 'half' | 'start' }[]): void;
}

interface ScaleRange { min: number; max: number }

export function createGraphRenderer(
  canvas: HTMLCanvasElement,
  series: SeriesDef[],
): GraphRenderer {
  const ctx = canvas.getContext('2d')!;
  const data: number[][] = series.map(() => []);
  const times: number[] = [];
  let width = canvas.width;
  let height = canvas.height;
  let viewStart: number | null = null;
  let viewEnd: number | null = null;
  let markers: { time: number; type: 'full' | 'half' | 'start' }[] = [];

  function push(values: number[], time: number): void {
    times.push(time);
    for (let i = 0; i < series.length && i < values.length; i++) {
      data[i].push(values[i]);
    }
  }

  function reset(): void {
    for (const d of data) d.length = 0;
    times.length = 0;
  }

  function getScale(arr: number[]): ScaleRange {
    let min = Infinity, max = -Infinity;
    for (let i = 0; i < arr.length; i++) {
      if (viewStart !== null && times[i] < viewStart) continue;
      if (viewEnd !== null && times[i] > viewEnd) continue;
      const v = arr[i];
      if (isFinite(v)) { if (v < min) min = v; if (v > max) max = v; }
    }
    if (!isFinite(min)) return { min: -1, max: 1 };
    const range = max - min;
    const pad = range === 0 ? 1 : range * 0.1;
    return { min: min - pad, max: max + pad };
  }

  function lastInView(arr: number[]): number | undefined {
    for (let i = arr.length - 1; i >= 0; i--) {
      const t = times[i];
      if (viewStart !== null && t < viewStart) break;
      if (viewEnd !== null && t > viewEnd) continue;
      return arr[i];
    }
    return undefined;
  }

  function draw(): void {
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(244,240,228,1)';
    ctx.fillRect(0, 0, width, height);

    const n = data[0]?.length ?? 0;
    if (n < 2) return;

    const tStart = viewStart ?? times[0];
    const tEnd = viewEnd ?? times[n - 1];
    const tRange = tEnd - tStart;

    ctx.strokeStyle = 'rgba(133,96,49,0.16)';
    ctx.lineWidth = 0.6;
    for (let i = 0; i <= 4; i++) {
      const y = (height / 4) * i;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
    }

    for (let s = 0; s < series.length; s++) {
      const scale = getScale(data[s]);
      const range = scale.max - scale.min;
      if (range === 0 || !isFinite(range)) continue;

      ctx.beginPath();
      ctx.strokeStyle = series[s].color;
      ctx.lineWidth = 1.6;
      let started = false;
      for (let i = 0; i < n; i++) {
        const t = times[i];
        if (t < tStart || t > tEnd) continue;
        const v = data[s][i];
        if (!isFinite(v)) continue;
        const x = tRange > 0 ? ((t - tStart) / tRange) * width : (i / (n - 1)) * width;
        const norm = (v - scale.min) / range;
        const y = height - norm * height;
        if (!started) { ctx.moveTo(x, y); started = true; }
        else ctx.lineTo(x, y);
      }
      if (started) ctx.stroke();
    }

    // marker lines
    const markerColors: Record<'full' | 'half' | 'start', string> = {
      full: 'rgba(42,68,120,0.85)',
      half: 'rgba(184,168,138,0.85)',
      start: 'rgba(158,58,58,0.85)',
    };
    for (const m of markers) {
      if (m.time < tStart || m.time > tEnd) continue;
      const x = tRange > 0 ? ((m.time - tStart) / tRange) * width : 0;
      ctx.strokeStyle = markerColors[m.type];
      ctx.setLineDash([2, 3]);
      ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
      ctx.setLineDash([]);
    }

    ctx.font = '500 12px "Cormorant Garamond", Georgia, serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    let xPos = 6;
    for (let s = 0; s < series.length; s++) {
      ctx.fillStyle = series[s].color;
      const val = lastInView(data[s]);
      const text = `${series[s].label} ${val !== undefined ? val.toFixed(2) : ''}`;
      ctx.fillText(text, xPos, 14);
      xPos += ctx.measureText(text).width + 14;
    }
  }

  function resize(w: number, h: number): void {
    width = w; height = h;
    canvas.width = w; canvas.height = h;
  }

  function setViewport(tStart: number | null, tEnd: number | null): void {
    viewStart = tStart;
    viewEnd = tEnd;
  }

  function setMarkers(m: { time: number; type: 'full' | 'half' | 'start' }[]): void {
    markers = m.slice();
  }

  return { push, reset, draw, resize, setViewport, setMarkers };
}
